import React, { useEffect, useMemo, useState } from 'react'
import { programService } from '../services/api'
import { CiueProfile, Program } from '../types'

type DetailKey = 'overview' | 'housing' | 'meals' | 'community' | 'expectations' | 'daily-life' | 'support'

type DetailConfig = {
  key: DetailKey
  label: string
  title: string
  subtitle: string
}

const detailConfig: DetailConfig[] = [
  { key: 'housing', label: 'Housing', title: 'Housing & Accommodations', subtitle: 'Where you will stay and how housing is supported' },
  { key: 'meals', label: 'Meals', title: 'Meals & Food Access', subtitle: 'Halal access, dining options, and grocery support' },
  { key: 'community', label: 'Community', title: 'Community & Student Life', subtitle: 'Cohort-based experience and faith-centered belonging' },
  { key: 'expectations', label: 'Expectations', title: 'Program Expectations', subtitle: 'Readiness, conduct, and application steps' },
  { key: 'daily-life', label: 'Daily Life', title: 'What Daily Life Looks Like', subtitle: 'A calm daily rhythm with study, prayer, and reflection' },
  { key: 'support', label: 'Support', title: 'Support & Belonging', subtitle: 'Academic, cultural, and spiritual support throughout' },
]

type FactRowProps = {
  label: string
  value: string
}

const FactRow: React.FC<FactRowProps> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-3 last:border-b-0">
    <span className="text-xs uppercase tracking-[0.25em] text-emerald-200/80 min-w-[140px]">
      {label}
    </span>
    <span className="text-sm text-white/85 text-right leading-relaxed">{value}</span>
  </div>
)

const getPreview = (text: string) => {
  const cleaned = text.trim()
  if (!cleaned) return ''
  const sentenceEnd = cleaned.search(/[.!?]\s/)
  if (sentenceEnd > 0) {
    return cleaned.slice(0, sentenceEnd + 1)
  }
  if (cleaned.length <= 120) return cleaned
  return `${cleaned.slice(0, 117)}...`
}

const StudentExperience: React.FC = () => {
  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeDetail, setActiveDetail] = useState<DetailKey>('overview')

  useEffect(() => {
    let active = true
    const fetchPrograms = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await programService.getPrograms()
        if (!active) return
        setPrograms(data)
      } catch (err) {
        console.error('Failed to load programs for student experience', err)
        if (!active) return
        setError('Unable to load program details right now.')
      } finally {
        if (active) setLoading(false)
      }
    }
    fetchPrograms()
    return () => {
      active = false
    }
  }, [])

  const selectedProgram = useMemo(() => {
    if (!programs.length) return null
    const featured = programs.find((program) => program.is_featured)
    return featured || programs[0]
  }, [programs])

  const ciue: CiueProfile = selectedProgram?.ciue_profile || {}

  const renderValue = (value: string | undefined, fallback: string) => (
    value && value.trim() ? value : fallback
  )

  const stripBracketPrefix = (text: string) => text.replace(/^\s*\[[^\]]+\]\s*/i, '').trim()

  const renderDetailValue = (value: string | undefined, fallback: string) => (
    stripBracketPrefix(renderValue(value, fallback))
  )

  const mealsIncludedLabel = (() => {
    if (selectedProgram?.meals_included === true) return 'Meals included'
    if (selectedProgram?.meals_included === false) return 'Meals not included'
    return 'Meal plan shared on admission'
  })()

  const hasDailyOverview = Boolean(ciue.daily_schedule_overview && ciue.daily_schedule_overview.trim())

  const overviewSummary = 'A quick overview of what life in the program looks like.'

  const toShortPhrase = (value: string | undefined, fallback: string, maxWords = 5) => {
    const candidate = value && value.trim() ? value : fallback
    const bracketMatch = candidate.match(/^\s*\[([^\]]+)\]\s*/i)
    const raw = bracketMatch ? bracketMatch[1] : candidate
    const cleaned = raw.replace(/[.!?]+$/, '').trim()
    const words = cleaned.split(/\s+/).slice(0, maxWords)
    return words.join(' ')
  }

  const overviewFacts = [
    { label: 'Housing', value: toShortPhrase(selectedProgram?.housing_type || undefined, 'On-campus hostel') },
    { label: 'Meals', value: toShortPhrase(ciue.halal_food_access, mealsIncludedLabel === 'Meals included' ? 'Meals included' : 'Not included') },
    { label: 'Meal plan', value: toShortPhrase(mealsIncludedLabel, 'Meal plan shared') },
    { label: 'Community', value: toShortPhrase(ciue.social_support, 'Cohort-based peer model') },
    { label: 'Expectations', value: toShortPhrase(ciue.expected_conduct, 'Active participation required') },
    { label: 'Daily life', value: toShortPhrase(ciue.daily_schedule_overview, 'Structured daily rhythm') },
    { label: 'Support', value: toShortPhrase(ciue.logistical_support, 'On-the-ground support') },
  ]

  const detailFacts = (key: DetailKey) => {
    if (key === 'overview') {
      return overviewFacts
    }
    if (key === 'housing') {
      return [
        { label: 'Housing type', value: renderDetailValue(selectedProgram?.housing_type || undefined, 'Details on admission') },
        { label: 'Location', value: renderDetailValue(ciue.housing_location, 'Final location shared pre-departure') },
        { label: 'Room setup', value: renderDetailValue(ciue.room_arrangement, 'Room arrangement shared with cohort') },
        { label: 'Prayer access', value: renderDetailValue(ciue.prayer_access || selectedProgram?.prayer_environment || undefined, 'Prayer-friendly access provided') },
        { label: 'Facilities', value: renderDetailValue(ciue.nearby_facilities, 'Nearby facilities shared pre-travel') },
        { label: 'Support', value: renderDetailValue(ciue.housing_support, 'Housing support contacts provided') },
      ]
    }
    if (key === 'meals') {
      return [
        { label: 'Meals', value: mealsIncludedLabel },
        { label: 'Halal access', value: renderDetailValue(ciue.halal_food_access, 'Halal options confirmed') },
        { label: 'Dining options', value: renderDetailValue(selectedProgram?.food_description || undefined, 'Campus and local halal dining') },
        { label: 'Groceries', value: renderDetailValue(ciue.grocery_access, 'Grocery access nearby') },
        { label: 'Meal plan', value: renderDetailValue(ciue.meal_plan_details, 'Meal routines shared pre-departure') },
      ]
    }
    if (key === 'community') {
      return [
        { label: 'Community', value: renderDetailValue(ciue.social_support, 'Peer support and cohort community') },
        { label: 'Muslim presence', value: renderDetailValue(ciue.muslim_community_presence, 'Local Muslim community connection') },
        { label: 'Student orgs', value: renderDetailValue(ciue.student_orgs, 'Student orgs and halaqas') },
        { label: 'Cohort life', value: renderDetailValue(selectedProgram?.community_life_description || undefined, 'Social and cultural events') },
      ]
    }
    if (key === 'expectations') {
      return [
        { label: 'Islamic studies', value: renderDetailValue(ciue.prior_islamic_studies, 'Requirements vary by cohort') },
        { label: 'Arabic', value: renderDetailValue(ciue.arabic_requirement_details, 'Arabic requirement listed per cohort') },
        { label: 'Conduct', value: renderDetailValue(ciue.expected_conduct, 'Respectful engagement expected') },
        { label: 'Travel readiness', value: renderDetailValue(ciue.travel_readiness, 'International travel readiness') },
        { label: 'Application', value: renderDetailValue(ciue.application_steps, 'Inquiry → application → interview') },
        { label: 'Deadlines', value: renderDetailValue(ciue.selection_process, 'Cohort timelines provided') },
      ]
    }
    if (key === 'daily-life') {
      return [
        { label: 'Morning', value: 'Classes and study sessions' },
        { label: 'Midday', value: 'Prayer break and lunch' },
        { label: 'Afternoon', value: 'Lectures or excursions' },
        { label: 'Evening', value: 'Community and reflection' },
        { label: 'Schedule', value: hasDailyOverview ? 'Detailed rhythm shared pre-departure' : 'Schedule varies by cohort' },
      ]
    }
    return [
      { label: 'Logistics', value: renderDetailValue(ciue.logistical_support, 'On-the-ground coordination') },
      { label: 'Academic', value: renderDetailValue(ciue.academic_support, 'Study guidance and advising') },
      { label: 'Cultural', value: renderDetailValue(ciue.cultural_support, 'Cultural adjustment support') },
      { label: 'Spiritual', value: renderDetailValue(ciue.spiritual_support || selectedProgram?.prayer_environment || undefined, 'Prayer-friendly environment') },
      { label: 'Representation', value: renderDetailValue(ciue.representation_commitment, 'Inclusive and respectful cohort culture') },
    ]
  }

  const detailSummary = (key: DetailKey) => {
    if (key === 'overview') {
      return overviewSummary
    }
    if (key === 'housing') {
      return getPreview(renderDetailValue(selectedProgram?.housing_description || ciue.housing_support, 'Housing details shared upon acceptance.'))
    }
    if (key === 'meals') {
      return getPreview(renderDetailValue(selectedProgram?.food_description || ciue.meal_plan_details, 'Halal access and meal routines shared before departure.'))
    }
    if (key === 'community') {
      return getPreview(renderDetailValue(selectedProgram?.community_life_description || ciue.social_support, 'Cohort-centered community and faith-based support.'))
    }
    if (key === 'expectations') {
      return getPreview(renderDetailValue(ciue.program_expectations || selectedProgram?.ideal_participant_description || undefined, 'Clear expectations and readiness criteria for applicants.'))
    }
    if (key === 'daily-life') {
      return getPreview(renderDetailValue(ciue.daily_schedule_overview || selectedProgram?.weekly_structure_description || undefined, 'Balanced daily rhythm with study, prayer, and reflection.'))
    }
    return getPreview(renderDetailValue(ciue.inclusion_statement, 'Inclusive academic, cultural, and spiritual support.'))
  }

  const activeTitle = activeDetail === 'overview' ? 'Program Overview' : (detailConfig.find((tab) => tab.key === activeDetail)?.title || '')
  const activeSubtitle = activeDetail === 'overview' ? 'At-a-glance summary' : (detailConfig.find((tab) => tab.key === activeDetail)?.subtitle || '')

  const contentSummary = detailSummary(activeDetail)
  const contentFacts = detailFacts(activeDetail)

  return (
    <section id="student-experience" className="py-24 px-6 min-h-screen">
      {/* CIUE: Life in the Program */}
      <div className="max-w-5xl mx-auto w-full">
        <p className="text-xs uppercase tracking-[0.5em] text-emerald-300 text-center">Life in the Program</p>
        <h2 className="text-white text-2xl sm:text-3xl text-center font-semibold mt-3">
          Student Experience, Support, and Daily Life
        </h2>

        {loading && (
          <div className="text-center text-white/80 py-6">Loading program experience details...</div>
        )}

        {error && (
          <div className="text-center text-red-200 bg-red-900/30 border border-red-500/40 rounded-xl p-4 mt-6">
            {error}
          </div>
        )}

        <div className="mt-10 rounded-3xl border border-white/10 bg-white/5/80 backdrop-blur-xl shadow-[0_20px_60px_rgba(15,23,42,0.45)]">
          <div className="px-4 sm:px-6 py-6">
            <div className="transition-opacity duration-300">
              <div className="flex flex-col gap-4">
                <button
                  type="button"
                  onClick={() => setActiveDetail('overview')}
                  className={
                    activeDetail === 'overview'
                      ? 'w-full rounded-2xl border border-emerald-200/40 bg-emerald-400/15 text-emerald-50 px-5 py-4 text-left text-lg font-semibold shadow-[0_12px_30px_rgba(16,185,129,0.15)] transition'
                      : 'w-full rounded-2xl border border-white/15 bg-white/5 text-white/80 px-5 py-4 text-left text-lg font-semibold hover:border-white/30 transition'
                  }
                >
                  Program Overview
                </button>

                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-white/50">Explore details</p>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {detailConfig.map((tab) => {
                      const isActive = tab.key === activeDetail
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveDetail(tab.key)}
                          className={
                            isActive
                              ? 'px-3 py-2 rounded-full bg-emerald-500/20 border border-emerald-300/60 text-emerald-100 text-sm font-semibold transition'
                              : 'px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white/70 text-sm font-medium hover:text-white hover:border-white/30 transition'
                          }
                        >
                          {tab.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-white text-2xl font-semibold">{activeTitle}</h3>
                    <p className="text-white/60 text-sm mt-2">{activeSubtitle}</p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr] gap-8">
                  <div>
                    <p className="text-white/75 text-sm leading-relaxed">{contentSummary}</p>
                  </div>
                  <div className="space-y-3">
                    {contentFacts.map((fact) => (
                      <FactRow key={fact.label} label={fact.label} value={fact.value} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default StudentExperience
